/**
 * /solo/head-coach — Parent/Guardian Head Coach Dashboard.
 *
 *  Left rail — student picker (parent may have multiple)
 *  Main:
 *    • Live activity feed (last 5 completed lessons)
 *    • Time-on-task vs state minimum (today + this week)
 *    • Attendance calendar (last 30 days) — green present / red absent / blue travel
 *    • Flag Travel Absence modal
 *    • Pacing & compliance card
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Plane, Clock, Flame, Calendar as CalIcon,
  Check, AlertTriangle, TrendingUp, X,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const HeadCoachDashboard = () => {
  const [params] = useSearchParams();
  const [studentEmail, setStudentEmail] = useState(params.get('student') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTravel, setShowTravel] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = studentEmail
        ? `/academy/solo/head-coach/dashboard?student_email=${encodeURIComponent(studentEmail)}`
        : '/academy/solo/head-coach/dashboard';
      const r = await api.get(url, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail) toast.error(detail);
      setData(null);
    } finally { setLoading(false); }
  }, [studentEmail]);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  if (!data?.student) return (
    <NoStudent onSearch={setStudentEmail} />
  );

  const s = data.student;
  const p = data.pacing || {};
  const minutesPct = data.state_min_minutes ? Math.min(100, Math.round(100 * (data.today?.minutes || 0) / data.state_min_minutes)) : 0;
  const behind = (p.day_N || 0) > (data.activity?.length || 0);  // approximate

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24" data-testid="head-coach-dashboard">
      <div className="border-b-2 border-zinc-900 py-8 px-6" style={{ background: 'linear-gradient(to bottom, rgba(59,130,246,0.06), transparent)' }}>
        <div className="container mx-auto max-w-6xl">
          <Link to="/solo/enroll" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-3">
            <ArrowLeft className="w-3 h-3" /> Enrollment
          </Link>
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-blue-400">Head Coach · Guardian Portal</div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.02] mt-2">
            {s.name || s.email}
          </h1>
          <div className="text-[11px] text-zinc-500 mt-2 flex flex-wrap gap-x-4 gap-y-1">
            <span>Track {s.track}</span>
            <span>Grade {s.grade_level}</span>
            <span>{s.state_of_record}</span>
            {s.charter_partner && <span>Charter · {s.charter_partner}</span>}
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-6 py-8 space-y-8">
        {/* KPI strip */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={<Clock className="w-4 h-4" />} label="Today" value={`${data.today?.minutes || 0}`}
            sub={`/ ${data.state_min_minutes} min`} pct={minutesPct}
            good={data.today?.present} testid="kpi-today" />
          <Kpi icon={<Flame className="w-4 h-4" />} label="This Week" value={`${data.week_minutes || 0}`}
            sub="minutes" testid="kpi-week" />
          <Kpi icon={<TrendingUp className="w-4 h-4" />} label="Day" value={`${(p.day_N || 0) + 1}`}
            sub={`/ ${p.total_days || 180}`} pct={p.percent_complete} testid="kpi-day" />
          <Kpi icon={behind ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
            label="Pacing" value={behind ? 'Behind' : 'On Pace'}
            sub={`${p.max_unlocked_lesson + 1 || 0} max unlocked`}
            danger={behind} good={!behind} testid="kpi-pacing" />
        </section>

        {/* Flag travel button */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowTravel(true)}
            className="border-2 border-blue-500/60 text-blue-400 hover:bg-blue-500 hover:text-black px-4 py-2 text-[11px] font-black uppercase tracking-[0.2em] flex items-center gap-2"
            data-testid="flag-travel-btn">
            <Plane className="w-3.5 h-3.5" /> Flag Travel Absence
          </button>
        </div>

        {/* Activity feed */}
        <section>
          <SectionTitle>Recent Activity</SectionTitle>
          {(data.activity || []).length === 0 ? (
            <div className="border-2 border-zinc-900 bg-zinc-950 p-6 text-center">
              <div className="text-[11px] text-zinc-500">No lessons completed yet. First rep coming up.</div>
            </div>
          ) : (
            <div className="border-2 border-zinc-900 divide-y-2 divide-zinc-900">
              {(data.activity || []).map((a) => (
                <div key={a.lesson_id} className="px-4 py-3 flex items-center gap-3" data-testid={`activity-${a.lesson_id}`}>
                  <Check className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black text-white truncate">{a.title || a.lesson_id}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      {a.subject} · {new Date(a.completed_at).toLocaleString()}
                    </div>
                  </div>
                  {a.score != null && (
                    <div className="text-[11px] font-black text-orange-500">{Math.round(a.score * 100)}%</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Attendance calendar */}
        <section>
          <SectionTitle>Attendance · Last 30 Days</SectionTitle>
          <div className="border-2 border-zinc-900 bg-zinc-950 p-4">
            <div className="grid grid-cols-15 md:grid-cols-30 gap-1" style={{ gridTemplateColumns: 'repeat(30, minmax(0, 1fr))' }}>
              {(data.calendar || []).map((c) => (
                <div key={c.date} title={`${c.date} · ${c.minutes}m · ${c.travel ? 'travel' : (c.present ? 'present' : 'absent')}`}
                  className={`aspect-square rounded-sm ${c.travel ? 'bg-blue-500/70' : (c.present ? 'bg-orange-500' : (c.minutes > 0 ? 'bg-zinc-700' : 'bg-zinc-900'))}`} />
              ))}
            </div>
            <div className="flex items-center gap-4 mt-4 text-[10px] uppercase tracking-widest text-zinc-500 font-black">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-orange-500 rounded-sm" /> Present</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-zinc-700 rounded-sm" /> Partial</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-blue-500/70 rounded-sm" /> Travel</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-zinc-900 border border-zinc-800 rounded-sm" /> Absent</span>
            </div>
          </div>
        </section>

        {/* Travel flags list */}
        {(data.travel_flags || []).length > 0 && (
          <section>
            <SectionTitle>Travel Flags</SectionTitle>
            <div className="border-2 border-zinc-900 divide-y-2 divide-zinc-900">
              {(data.travel_flags || []).map((f) => (
                <div key={f.id} className="px-4 py-3 flex items-center gap-3">
                  <Plane className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black text-white">{f.start_date} → {f.end_date}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{f.days} day{f.days !== 1 ? 's' : ''} · {f.reason}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {showTravel && (
        <TravelModal studentEmail={s.email} onClose={() => setShowTravel(false)}
          onSaved={() => { setShowTravel(false); load(); toast.success('Travel window flagged'); }} />
      )}
    </div>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3">{children}</div>
);

const Kpi = ({ icon, label, value, sub, pct, good, danger, testid }) => (
  <div className={`border-2 p-4 ${danger ? 'border-red-500/40 bg-red-500/5' : (good ? 'border-orange-500/40 bg-orange-500/5' : 'border-zinc-900 bg-zinc-950')}`} data-testid={testid}>
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black flex items-center gap-1.5">{icon} {label}</div>
    <div className={`text-3xl font-black mt-1.5 ${danger ? 'text-red-400' : (good ? 'text-orange-500' : 'text-white')}`}>{value}</div>
    <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>
    {pct != null && (
      <div className="w-full h-1 bg-black mt-3">
        <div className={`h-full ${danger ? 'bg-red-500' : 'bg-orange-500'}`} style={{ width: `${pct}%` }} />
      </div>
    )}
  </div>
);

const NoStudent = ({ onSearch }) => {
  const [email, setEmail] = useState('');
  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-24 pb-24" data-testid="no-student">
      <div className="container mx-auto max-w-lg px-6 text-center">
        <h1 className="text-3xl font-black uppercase tracking-tight mb-3">Head Coach Portal</h1>
        <p className="text-sm text-zinc-400 mb-6">Enter your student's SOLO email to open their dashboard.</p>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="student@example.com"
          className="w-full bg-black border-2 border-zinc-800 px-3 py-3 text-sm mb-3 focus:border-orange-500 outline-none"
          data-testid="student-email-input" />
        <button onClick={() => onSearch(email.trim())}
          className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[11px] font-black uppercase tracking-[0.2em]"
          data-testid="open-student-btn">
          Open Dashboard
        </button>
      </div>
    </div>
  );
};

const TravelModal = ({ studentEmail, onClose, onSaved }) => {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('family_travel');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!start || !end) { toast.error('Both start and end date required'); return; }
    setBusy(true);
    try {
      await api.post('/academy/solo/travel/flag', {
        student_email: studentEmail, start_date: start, end_date: end, reason,
      }, { headers: authHeaders() });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not flag travel');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0B0E14] border-2 border-orange-500 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="travel-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">Flag Travel Absence</div>
            <div className="text-lg font-black mt-1">Pause the pacing calendar</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="travel-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-zinc-400 mb-4 leading-relaxed">
          Days in this window will be marked "excused travel" and won't count against the pacing calendar.
        </p>
        <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block">Start Date</label>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
          className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm mb-3 focus:border-orange-500 outline-none"
          data-testid="travel-start" />
        <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block">End Date</label>
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
          className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm mb-3 focus:border-orange-500 outline-none"
          data-testid="travel-end" />
        <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block">Reason</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm mb-4 focus:border-orange-500 outline-none"
          data-testid="travel-reason">
          <option value="family_travel">Family Travel</option>
          <option value="tournament">Sports Tournament</option>
          <option value="illness">Illness</option>
          <option value="family_emergency">Family Emergency</option>
          <option value="other">Other</option>
        </select>
        <button onClick={submit} disabled={busy}
          className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="travel-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plane className="w-3.5 h-3.5" />}
          Flag Travel
        </button>
      </div>
    </div>
  );
};

export default HeadCoachDashboard;
